import React from 'react';
import * as Styles from './review-response.less';
import { useDependencies } from '@servicetitan/react-ioc';
import { ServiceFinanceStore, ApplicationStatus } from '../../../stores/service-finance.store';
import { Icon } from '@material-ui/core';
import Moment from 'moment';
import { formatMoney } from 'accounting';

interface StatusDisplay {
    icon: string;
    header: string;
    title: string;
    message?: string | undefined;
}

const getStatusDisplay = (status: ApplicationStatus | undefined): StatusDisplay => {
    switch (status) {
        case ApplicationStatus.Approved:
            return {
                icon: 'done',
                header: 'Congratulations, your loan has been approved!',
                title: 'Approved',
            };
        case ApplicationStatus.Complete:
            return {
                icon: 'done',
                header: 'Congratulations, your application is complete!',
                title: 'Complete',
            };
        case ApplicationStatus.Declined:
            return {
                icon: 'error_outline',
                header: 'Service Finance is unable to approve this application.',
                title: 'Declined',
            };
        case ApplicationStatus.CounterOffer:
            return {
                icon: 'error_outline',
                header: 'Counteroffer received.',
                title: 'Review',
            };
        case ApplicationStatus.UnderReview:
            return {
                icon: 'error_outline',
                header: 'Your application is under review.',
                title: 'Under Review',
                message:
                    'For more details, contact Service Finance at 866-254-0497 option 4 for more details. \nHours of operations are from 5am ET - 11:59pm ET.',
            };
        case ApplicationStatus.External:
            return {
                icon: 'error_outline',
                header: 'Please visit Service Finance to sign your loan document.',
                title: 'Sign Document',
                message: '',
            };
        case ApplicationStatus.NoConsent:
            return {
                icon: 'error_outline',
                header: 'ESign disclosure and Consent not received',
                title: 'No ESign Consent',
                message:
                    'To receive a paper copy at no charge, please request it in one of the following ways:\n\n• Send an e-mail message with your name and mailing address to info@svcfin.com\n• Call Service Finance at (866) 254-0497\n• Fax Service Finance at (866) 257-8122\n• Or write to Service Finance at Service Finance Company, LLC at\n\n  555 South Federal Highway,\n  Suite 200,\n  Boca Raton, Florida 33432\n\n  with your name and mailing address. Be sure to state that you are requesting a copy\n  of the Contract Documents.',
            };
        case ApplicationStatus.ProblemProcessingRequest:
            return {
                icon: 'error_outline',
                header: 'We ran into problems while processing your application.',
                title: 'Error',
                message: 'We could not process your application at this time. \nYou may contact Service Finance at 866-254-0497.'
            };
        default:
            return {
                icon: 'error_outline',
                header: 'Error communicating with Service Finance',
                title: 'Error',
                message:
                    'Please confirm your device is connected to the Internet and try again. \nOr contact Service Finance at 866-254-0497.',
            };
    }
};

export const ReviewResponse: React.FC = () => {
    const [store] = useDependencies(ServiceFinanceStore);
    const statusDisplay = getStatusDisplay(store.applicationStatus);

    if (store.applicationStatus === ApplicationStatus.ProblemProcessingRequest) {
        statusDisplay.message = store.errorMessage ?? statusDisplay.message;
    }

    return (
        <div>
            <Icon className={Styles.icon}>{statusDisplay.icon}</Icon>
            <div className={Styles.header}>{statusDisplay.header}</div>
            {![ApplicationStatus.NoConsent, 
                ApplicationStatus.ConnectionError, 
                ApplicationStatus.ProblemProcessingRequest].includes(
                store.applicationStatus!
            ) && (
                <div>
                    <table className={Styles.table}>
                        <tbody>
                            <tr className={Styles.row}>
                                <th className={Styles.tableHeader}>Application ID</th>
                                <td className={Styles.data}>{store.externalApplicationId}</td>
                            </tr>
                            <tr className={Styles.row}>
                                <th className={Styles.tableHeader}>Current Date</th>
                                <td className={Styles.data}>{Moment(new Date()).format('L')}</td>
                            </tr>
                            <tr className={Styles.row}>
                                <th className={Styles.tableHeader}>Status</th>
                                <td className={Styles.data}>{statusDisplay.title}</td>
                            </tr>
                            <tr className={Styles.row}>
                                <th className={Styles.tableHeader}>Finance Type</th>
                                <td className={Styles.data}>{store.selectedPlan!.description}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            )}
            {statusDisplay.message && (
                <pre
                    className={
                        store.applicationStatus === ApplicationStatus.NoConsent
                            ? Styles.messageNoConsent
                            : Styles.message
                    }
                >
                    {statusDisplay.message}
                </pre>
            )}
            {store.offer && (
                <div className={Styles.offerInfo}>
                    <div>
                        <div className={Styles.infoTitle}>Amount Financed</div>
                        <div className={Styles.infoData}>
                            {formatMoney(store.offer.AmountFinanced)}
                        </div>
                    </div>
                    <div>
                        <div className={Styles.infoTitle}>Terms</div>
                        <div className={Styles.infoData}>{store.offer.Term} months</div>
                    </div>
                    <div>
                        <div className={Styles.infoTitle}>APR</div>
                        <div className={Styles.infoData}>{store.offer.Apr}</div>
                    </div>
                    <div>
                        <div className={Styles.infoTitle}>Monthly Payment</div>
                        <div className={Styles.infoData}>
                            {formatMoney(store.offer.MonthlyPayment)}
                        </div>
                    </div>
                    <div>
                        <div className={Styles.infoTitle}>Max Credit Amount</div>
                        <div className={Styles.infoData}>
                            {formatMoney(store.offer.MaxCreditAmount)}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
